import * as R from 'ramda'
import {
  constantToSentenceCase,
  camelCaseToSentenceCase,
  mapObjectKeys,
  stateNameMapping,
} from 'sidecar-utility-library'

const commonFields = [
  'uuid',
  'chainCode',
  'name',
  'pharmacyRate',
  'priceFairness',
  'image',
  'sidecarCost',
  'memberPays',
  'nonFacilityCostAfterSidecar',
]
export const parsePharmacyList = R.pipe(
  // groups all the doctors by their name field
  R.groupBy(R.prop('name')),
  // sort the doctors by cheap and close first
  R.map(
    R.sortBy(
      R.converge(R.multiply, [
        R.propOr(1, 'distance'),
        R.propOr(1, 'pharmacyRate'),
      ]),
    ),
  ),
  // turns the sorted map into tuples [name, pharmacies]
  R.toPairs,
  // pulls out the common fields from each pharmacy and
  // includes it at the group root, then puts all the
  // pharmacies into a locations array.
  R.map(([__, pharmacies]) => ({
    ...R.pick(commonFields, pharmacies[0]),
    locations: pharmacies,
  })),
  arr => arr,
)

const filterKeys = [
  'type',
  'receiveAt',
  'diagnosis',
  'version',
  'brand',
  'form',
  'dosage',
  'quantity',
]

const isConstantRegex = input => {
  const regex = /[A-Z]+_[A-Z]+/
  return regex.test(input)
}

export const parseCareDetail = R.pipe(
  // first create a list of filters based on care details
  // these filters will be saved with the care detail
  mapObjectKeys({
    filters: R.pipe(
      // pick from the filterKeys above
      R.pick(filterKeys),
      // for each of the filterKeys found in the care item,
      // turn the filterKey and it’s value into pairs (i.e. [filterKey, options])
      R.toPairs,
      // turn the pairs into a list of objects
      // that will become our filters.
      R.map(([filterKey, options]) => ({
        name: filterKey,
        label: camelCaseToSentenceCase(useCorrectFiltersLabel(filterKey)),
        // because we don’t get any values passed in, we want
        // to turn the label that we have, into our value.
        options: R.pipe(
          R.map(
            mapObjectKeys({
              // making the label human readible, because it is simply display, should not affect data
              label: R.pipe(R.prop('label'), label => {
                // for brand specific filters, we want to add (generic) or (brand) to the label
                if (filterKey === 'brand') {
                  options.forEach(option => {
                    // in the off chance scriptsave doesn't return a queryMap, return the label as is
                    if (R.isNil(option.queryMap)) {
                      return
                    }
                    // LIGHTS-584 added brand or generic txt to drug label options
                    if (label === option.queryMap.drugName) {
                      if (option.queryMap.brandedOrGenericId === 'G') {
                        label = label + ' (generic)'
                      }
                      if (option.queryMap.brandedOrGenericId === 'B') {
                        label = label + ' (brand)'
                      }
                    }
                  })
                }

                // Only convert to sentence case if we have a constant in the label, otherwise we may
                // lowercase a string that was not intended to display in lowercase (ie. 'OTC COVID-19' should
                // not appear as 'Otc covid-19')
                return isConstantRegex(label)
                  ? constantToSentenceCase(label)
                  : label
              }),
              subLabel: R.prop('subLabel'),
              value: option =>
                `${R.prop('label')(option)}${
                  R.prop('subLabel')(option) || ''
                }`.toLowerCase(), //This is to establish a unique key to select appropriate option in case of label/sublabel option
              title: R.pipe(R.prop('label'), label => {
                // Only convert to sentence case if we have a constant in the label, otherwise we may
                // lowercase a string that was not intended to display in lowercase (ie. 'OTC COVID-19' should
                // not appear as 'Otc covid-19')
                return isConstantRegex(label)
                  ? constantToSentenceCase(label)
                  : label
              }),
            }),
          ),
          R.sort((a, b) => (a.label < b.label ? -1 : 1)),
        )(options),
      })),
      R.map((filter = {}) => {
        // When ScriptSave does not return a quantity to us, the backend
        // defaults the drug’s quantity to 1 in care details and preview
        // coverage. This is just making sure this is what gets displayed

        if (filter.name === 'quantity' && R.isEmpty(filter.options)) {
          return {
            ...filter,
            options: [
              {
                label: 1,
                selected: true,
                title: 1,
                value: null,
              },
            ],
          }
        }
        return filter
      }),
    ),
  }),
  // parse the rest based on what kind of care we have
  R.evolve({
    // if the care detail has pharmacies, parse those
    // we want to group a flat list of pharmacies, like many
    // walmarts or rite-aids and combine them into one,
    // grouped by their name.
    pharmacies: R.pipe(
      // group the pharmacies
      // then store in as ordered map to use in a dropdown
      parsePharmacyList,
    ),
    // if the care detail has facilities, parse those
    // we're going to just turn the list of facilities
    // into an ordered map so we can use it in a dropdown.
    facilities: arr => arr,
  }),

  // because we only want the filters that we've parsed out
  // we'll get rid of the things we parsed those filters from.
  // we don't need them anymore.
  R.omit(filterKeys),
)

export const useCorrectFiltersLabel = label => {
  switch (label) {
    case 'diagnosis': // we are using Admission for instead of Diagnosis for care details
      return 'admissionFor'
    default:
      return label
  }
}

export const abbreviatedState = state =>
  state.length === 2 ? state : stateNameMapping[state]

/**
 * Supplemental Function for transforming the string description of a prechecked
 * provider into the following format: eg.'X-ray, MRI, & ultrasound diagnostic
 * imaging services' Accepts a string and returns a transformed string The first
 * word is capitalized and all other words are lowercased Acronyms are kept as
 * is
 *
 * @param {string} str
 */
export const transformString = str => {
  const words = str.split(' ')

  return words
    .map((word, index) => {
      // Keep the first word capitalized as is
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      }
      // Check if the word is in all uppercase letters (likely an acronym)
      if (word === word.toUpperCase() && word.length > 1) {
        return word // Keep acronyms as is
      }
      // Lowercase all other non-acronym words
      return word.toLowerCase()
    })
    .join(' ')
}

/**
 * Accepts an array of pre-checked providers and filters out any duplicates
 * where the providerName and categoryType are the same Combines the
 * descriptions when the provider exists and shares a common prefix in
 * categoryName
 *
 * @param {array} preCheckedProviders Pre-checked providers from the search
 *   results
 */
export const filterDuplicateProviders = preCheckedProviders => {
  // Filter out duplicate providers
  const uniquePrecheckedProviders = preCheckedProviders.filter(
    (provider, index, self) =>
      index ===
      self.findIndex(
        p =>
          p.providerUuid === provider.providerUuid &&
          p.categoryName === provider.categoryName &&
          p.categoryType === provider.categoryType,
      ),
  )

  // filtered providers with updated descriptions will be stored here
  const filteredProviders = []

  uniquePrecheckedProviders.forEach(provider => {
    const existingProvider = filteredProviders.find(
      filteredProvider =>
        filteredProvider.providerUuid === provider.providerUuid &&
        filteredProvider.categoryType === provider.categoryType,
    )

    if (existingProvider) {
      // If a provider matches an existing provider, we want to combine the description
      // of the duplicate provider with the existing provider
      if (existingProvider.categoryName !== provider.categoryName) {
        // this is a special case for when we have more than 2 category names with the same provider
        // this combines the descriptions of the providers into 1 description
        // e.g. Diagnostic Imaging - X-ray, Diagnostic Imaging - MRI, Diagnostic Imaging - Ultrasound
        // e.g. the result will be 'X-ray, MRI, & ultrasound diagnostic imaging services'
        const alteredDescription = existingProvider.description.split(' ')
        // check if the description has been previously altered with a comma or ampersand
        if (alteredDescription[1] === '&' || alteredDescription[1] === ',') {
          const splitExistingDesc = existingProvider.description.split(' ')
          splitExistingDesc[0] = `${splitExistingDesc[0]},`
          const splitNewDesc = provider.description.split(' ')

          splitExistingDesc.splice(1, 0, `${splitNewDesc[0]},`)
          existingProvider.description = splitExistingDesc.join(' ')
        } else {
          const splitExistingDesc = existingProvider.description.split(' ')
          const splitNewDesc = provider.description.split(' ')

          splitExistingDesc.splice(1, 0, `& ${splitNewDesc[0]}`)
          existingProvider.description = transformString(
            splitExistingDesc.join(' '),
          )
        }
      }
    } else {
      filteredProviders.push(provider)
    }
  })

  return filteredProviders
}
